import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';
import { readTheme } from '../lib/theme';
import { requestPasswordReset } from '../lib/auth-api';
import {
  Particles,
  ClickSpark,
  AnimatedContent,
} from '../components/react-bits/index.js';
import styles from './Login.module.css';

// Ask for a reset link. Shares Login's card styling so the two pages read as one flow.
export function ForgotPassword() {
  const [theme] = useState(() => readTheme());
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch {
      setError('Could not reach the server. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ClickSpark sparkColor="#0979f5" sparkSize={10} sparkRadius={22} sparkCount={10}>
      <main className={styles.root}>
        <div className={styles.studioGlow} aria-hidden />

        <div className={styles.loginBg} aria-hidden>
          <Particles
            particleCount={45}
            particleSpread={12}
            speed={0.1}
            particleColors={['#0979f5', '#0499f9', '#5d39f5', '#60a5fa']}
            moveParticlesOnHover={true}
            alphaParticles={true}
            particleBaseSize={100}
            disableRotation={false}
          />
        </div>

        <AnimatedContent
          trigger="mount"
          distance={20}
          duration={0.6}
          ease="power3.out"
          className={styles.authCard}
        >
          {/* Top Brand Header */}
          <div className={styles.topBrandRow}>
            <BrandLogo variant="wordmark" height={28} />
            <div className={styles.brandPill}>
              <span className={styles.brandPillDot} />
              <span>SECURITY & RECOVERY</span>
            </div>
          </div>

          {/* Title & Subtitle */}
          <div className={styles.headerBlock}>
            <h1 className={styles.title}>Reset your password</h1>
            <p className={styles.subtitle}>
              Enter the email you sign in with and we&rsquo;ll send you a link to choose a new password.
            </p>
          </div>

          {sent ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#10b981' }}>
                <CheckCircle2 size={22} />
                <span style={{ fontSize: 15, fontWeight: 700 }}>Check your inbox</span>
              </div>
              <p className={styles.subtitle} style={{ lineHeight: 1.6 }}>
                If an account exists for <strong style={{ color: theme === 'dark' ? '#f8fafc' : '#0f172a' }}>{email.trim()}</strong>, a reset link is on its way. It works once and expires in an hour.
              </p>
              <p className={styles.subtitle} style={{ fontSize: 13 }}>
                Nothing in your inbox? Check spam, then{' '}
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#0979f5',
                    fontWeight: 600,
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  try another address
                </button>
                .
              </p>
              <div className={styles.footerRow} style={{ justifyContent: 'center', marginTop: 8 }}>
                <Link to="/login" className={styles.footerLink}>
                  &larr; Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.field}>
                  <label htmlFor="reset-email" className={styles.label}>
                    Account Email
                  </label>
                  <input
                    id="reset-email"
                    className={styles.input}
                    type="email"
                    autoComplete="email"
                    required
                    autoFocus
                    placeholder="agent@yourcompany.internal"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </div>

                {error && (
                  <p className={styles.error} role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !email.trim()}
                  className={styles.submitBtn}
                >
                  <span>{submitting ? 'Sending reset link…' : 'Send reset link'}</span>
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              </form>

              <div className={styles.footerRow} style={{ justifyContent: 'center' }}>
                <Link to="/login" className={styles.footerLink}>
                  &larr; Back to sign in
                </Link>
              </div>
            </>
          )}
        </AnimatedContent>
      </main>
    </ClickSpark>
  );
}
